// RoastLog
import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { modbusSettings } from '$lib/PersistedState.svelte';
import { fahrenheitToCelsius } from '$lib/RLogUtils';
import { modbusDebugEnabled } from '$lib/State.svelte';
import { BaseDevice } from '../base';

// Others
import { invoke } from '@tauri-apps/api/core';

class ModbusClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ModbusClientError';
  }
}

class LoringDevice extends BaseDevice {
  type: DeviceType = DeviceType.LORING;
  private burnerPower: number | null = null;

  protected async onConnect(): Promise<boolean> {
    try {
      await invoke('connect_modbus', {
        host: modbusSettings.current.host
      });
      return true;
    } catch (error) {
      logger.error(`Failed to connect to Loring device: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    invoke('disconnect_modbus', {
      host: modbusSettings.current.host,
      port: this.getModbusPort()
    }).catch((error) => logger.warn(`Disconnect error: ${error}`));
  }

  protected async updateReadings(): Promise<void> {
    try {
      const modbusTemps = await this.readTemperatureHolding(
        modbusSettings.current.host,
        parseInt(modbusSettings.current.register),
        8
      );

      this.burnerPower = await this.readPowerHolding(modbusSettings.current.host, 778);

      // Loring is odd b/c it returns temperatures in F. To make everything simpler, convert the
      // original readings to C since the other devices output C.
      this.localReadings = [
        fahrenheitToCelsius(modbusTemps.bean),
        this.tcSettings?.[1].active && modbusTemps.inlet
          ? fahrenheitToCelsius(modbusTemps.inlet)
          : null,
        this.tcSettings?.[2].active && modbusTemps.stack
          ? fahrenheitToCelsius(modbusTemps.stack)
          : null,
        this.tcSettings?.[3].active && modbusTemps.return
          ? fahrenheitToCelsius(modbusTemps.return)
          : null
      ];
    } catch (error) {
      if (error instanceof ModbusClientError) {
        logger.error(`Modbus error: ${error.message}`);
      } else {
        logger.error(`Unknown error: ${error}`);
      }
      throw error;
    }
  }

  /**
   * Handle errors during polling. This is triggered when the updateReadings throws an error.
   */
  protected handleError(error: unknown): void {
    logger.warn(`Loring polling error: ${error}`);
  }

  protected async getPowerLevel(): Promise<number | null> {
    return this.burnerPower;
  }

  private async readPowerHolding(host: string, address: number): Promise<number | null> {
    try {
      const results: number[] = await invoke('read_modbus', {
        host,
        port: this.getModbusPort(),
        reg: address,
        count: 1
      });
      return parseInt(results[0].toString(16));
    } catch (error: unknown) {
      return null;
    }
  }

  private async readTemperatureHolding(host: string, address: number, count: number) {
    try {
      const results: number[] = await invoke('read_modbus', {
        host,
        port: this.getModbusPort(),
        reg: address,
        count
      });

      const bean = LoringDevice.convertTemperature(results[0], results[1]);

      if (modbusDebugEnabled.current === true) {
        logger.debug('DEVICE_LORING_READINGS', { results, bean });
      }

      return {
        bean,
        inlet: LoringDevice.convertTemperature(results[2], results[3]),
        stack: LoringDevice.convertTemperature(results[4], results[7]),
        return: LoringDevice.convertTemperature(results[6], results[5])
      };
    } catch (err: any) {
      const regex = /error creating modbus client/i;
      if (regex.test(err)) {
        throw new ModbusClientError('Could not create Modbus client');
      } else {
        logger.warn(`Modbus reading error: ${err}`);
      }
      return {
        bean: 0,
        inlet: null,
        stack: null,
        return: null
      };
    }
  }

  static convertTemperature(high: number, low: number): number {
    // Convert to hex strings, ensuring 4 digits with leading zeros
    const highHex = high.toString(16).padStart(4, '0');
    const lowHex = low.toString(16).padStart(4, '0');

    // Parse hex strings as decimal numbers (BCD interpretation)
    const highDecimal = parseInt(highHex);
    const lowDecimal = parseInt(lowHex);

    // Calculate the full temperature value
    const temperatureScaled = highDecimal * 10000 + lowDecimal;

    // Convert from scaled integer (×10) to actual temperature
    return temperatureScaled / 10.0;
  }
}

export default LoringDevice;
